"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";

/* Path dihitung dari app/layanan/mcu/ - tempat berkas INI berada.
     ../../components, ../../i18n  -> naik dua tingkat ke app/
     ./mcu                         -> tetangga di folder yang sama */
import Footer from "../../components/Footer";
import { useLanguage } from "../../i18n/LanguageProvider";
import { CATATAN_UMUM, KATEGORI, KONTAK_MCU, PERSIAPAN, TARIF_DIPERBARUI } from "./mcu";

/* ============================================================
   MEDICAL CHECK UP - /layanan/mcu

   Menggantikan halaman lama yang isinya enam lembar poster hasil
   pindaian. Poster itu bermasalah bukan karena jelek:

     - angka di dalam JPG tidak terbaca mesin pencari. Orang mencari
       "harga mcu jakarta selatan" dan halamanmu tidak muncul.
     - tidak terbaca pembaca layar.
     - harus dicubit-zoom di ponsel, tempat sebagian besar orang
       membacanya.
     - satu harga berubah = poster dibuat ulang di aplikasi desain,
       dan biasanya jadi tidak diubah.

   Keenam lembarnya tetap ada di tiap kategori, sebagai lampiran.

   ============================================================
   DUA KATEGORI BERDIRI SENDIRI-SENDIRI

   MCU General dipilih berdasarkan anggaran; MCU Sesuai Usia dipilih
   berdasarkan umur. Tidak ada yang perlu membandingkan "Paket C"
   dengan "Usia 18-25", jadi keduanya tidak pernah disatukan dalam
   satu tabel - masing-masing punya kartu, akordeon, daftar gratis,
   dan posternya sendiri.

   Satu-satunya yang menyatukan: kotak reservasi di bawah, karena di
   sana pengunjung memang sudah memutuskan.

   ============================================================
   TIGA LAPIS DI TIAP KATEGORI, DARI RINGKAS KE RINCI

     1. Kartu paket    - "paket mana yang saya butuhkan"
     2. Akordeon       - "apa saja yang diperiksa"
     3. Tabel tambahan - "kalau ditambah foto thorax, jadi berapa"

   Lapis ketiga hanya muncul kalau kategorinya memang punya pilihan
   tambahan. MCU Sesuai Usia tidak punya, jadi tabelnya tidak dirender
   sama sekali - bukan dirender kosong berisi tanda hubung.

   ============================================================
   className ditulis satu baris - lihat peringatan hydration di
   Facility.jsx. Jangan pakai string multi-baris di berkas ini.
   ============================================================ */

const LEBAR_HALAMAN = 1140;

/* ============================================================
   TAUTAN INDUK DI REMAH ROTI

   SUDAH true. app/layanan/page.jsx kini ADA, jadi "Fasilitas &
   Layanan" di remah roti boleh jadi tautan.

   Konstanta ini dulu false karena /layanan masih 404 - remah roti yang
   membawa pengunjung ke halaman rusak lebih buruk daripada remah roti
   tanpa tautan sama sekali. Alasan itu sudah tidak berlaku.

   TIDAK DIHAPUS meski nilainya sekarang tetap true. Percabangan di
   bawah menyimpan kedua bentuk - <Link> dan <span> - jadi kalau suatu
   saat /layanan dipindah atau dihapus, mematikan tautannya cukup satu
   kata di sini, bukan menyunting JSX di tengah berkas.

   Tiga berkas layanan lain memakai konstanta yang sama: IgdContent,
   MCUContent, LaboratoriumContent, dan SwabPcrContent - keempatnya
   sudah true. Kalau salah satunya diubah, ubah semuanya, supaya
   keempat halaman tidak berbeda perilaku.
   ============================================================ */
const TAUTKAN_INDUK = true;

const TEKS = {
  eyebrow: { id: "Layanan Kami", en: "Our Services" },
  judul: { id: "Medical Check Up (MCU)", en: "Medical Check Up (MCU)" },
  sub: {
    id: "Pemeriksaan kesehatan berkala di RS Jakarta Medical Center, tersedia dalam paket lengkap maupun paket yang disusun mengikuti kelompok usia.",
    en: "Routine health screening at RS Jakarta Medical Center, available as full packages or packages arranged by age group.",
  },
  mulaiDari: { id: "Mulai dari", en: "From" },
  hubungiCs: { id: "Hubungi Customer Service", en: "Contact Customer Service" },
  telepon: { id: "Telepon", en: "Call" },

  pemeriksaanJml: { id: "pemeriksaan", en: "examinations" },
  lihatRincian: { id: "Lihat rincian", en: "See details" },
  hubungi: { id: "Hubungi kami", en: "Contact us" },

  rincian: { id: "Rincian Pemeriksaan", en: "What Each Package Covers" },
  belumAda: {
    id: "Rincian pemeriksaan tersedia di customer service.",
    en: "Details are available from customer service.",
  },

  tabel: { id: "Harga Paket dan Tambahan", en: "Package and Add-on Prices" },
  /* Keterangan tabel dipecah dua. Kalimat pertama berlaku di kedua
     bentuk; kalimat kedua hanya berlaku di bentuk tabel, karena di
     bentuk ponsel tidak ada satu pun tanda hubung yang dirender -
     lihat catatan di TabelHarga. Menjelaskan tanda yang tidak
     terlihat justru membingungkan. */
  tabelKeterangan: {
    id: "Angka pada tabel adalah harga TOTAL paket beserta satu tambahannya, bukan biaya tambahannya saja.",
    en: "Figures show the TOTAL price of the package including one add-on, not the add-on fee alone.",
  },
  tabelKeteranganTanda: {
    id: "Tanda - berarti tambahan itu tidak tercantum untuk paket tersebut.",
    en: "A dash means the add-on is not listed for that package.",
  },
  pemeriksaan: { id: "Pemeriksaan", en: "Examination" },
  pilihPaket: { id: "Pilih paket", en: "Pick a package" },
  tidakTercantum: {
    id: "Tidak tercantum untuk paket ini",
    en: "Not listed for this package",
  },
  catatanHarga: {
    id: "Harga dapat berubah sewaktu-waktu. Mohon konfirmasi ke customer service sebelum melakukan reservasi.",
    en: "Prices may change at any time. Please confirm with customer service before booking.",
  },
  diperbarui: { id: "Tarif diperbarui", en: "Rates updated" },

  gratis: { id: "Termasuk Tanpa Biaya Tambahan", en: "Included at No Extra Cost" },
  catatan: { id: "Yang Perlu Diketahui", en: "Good to Know" },
  persiapan: { id: "Persiapan Sebelum MCU", en: "Before Your Check Up" },

  poster: { id: "Poster Paket", en: "Package Poster" },
  posterSub: {
    id: "Versi cetak dari daftar di atas. Klik untuk memperbesar.",
    en: "The printed version of the list above. Click to enlarge.",
  },
  tutup: { id: "Tutup", en: "Close" },
  sebelumnya: { id: "Sebelumnya", en: "Previous" },
  berikutnya: { id: "Berikutnya", en: "Next" },

  reservasi: { id: "Reservasi MCU", en: "Book a Check Up" },
  reservasiSub: {
    id: "Customer service kami membantu memilih paket dan mengatur jadwal pemeriksaan.",
    en: "Our customer service can help you pick a package and arrange a schedule.",
  },
  labelPaket: { id: "Paket", en: "Package" },
  labelTambahan: { id: "Tambahan", en: "Add-ons" },
  petunjukTambahan: {
    id: "Boleh pilih lebih dari satu. Untuk dua tambahan atau lebih, totalnya ditentukan customer service.",
    en: "You may pick more than one. For two or more add-ons, the total is set by customer service.",
  },
  tanpaTambahan: { id: "Tanpa tambahan", en: "No add-ons" },
  akanDikonfirmasi: {
    id: "Akan dikonfirmasi customer service",
    en: "To be confirmed by customer service",
  },
  labelPromo: { id: "Promo", en: "Promo" },
  /* Dua penutup pesan. Yang dipakai bergantung pada ada tidaknya
     angka - lihat komentar di dekat `penutup` di bawah. */
  penutupJadwal: {
    id: "Mohon informasi jadwal yang tersedia. Terima kasih.",
    en: "Could you let me know the available schedule? Thank you.",
  },
  penutupHarga: {
    id: "Mohon informasi harga paket ini beserta jadwal yang tersedia. Terima kasih.",
    en: "Could you let me know the price of this package and the available schedule? Thank you.",
  },
  totalnya: { id: "Total", en: "Total" },
  lanjutWa: { id: "Lanjut ke WhatsApp", en: "Continue on WhatsApp" },
  catatanReservasi: {
    id: "Harga mengikuti daftar di atas. Customer service akan mengonfirmasi sebelum jadwal ditetapkan.",
    en: "Prices follow the table above. Customer service will confirm before the schedule is set.",
  },
};

function pilihTeks(nilai, lang) {
  if (nilai == null) return "";
  if (typeof nilai === "string") return nilai;
  return nilai[lang] || nilai.id || "";
}

function rupiah(nilai) {
  if (nilai == null) return null;
  return `Rp ${new Intl.NumberFormat("id-ID").format(nilai)}`;
}

/* Tanda "+" di awal nama varian berguna sebagai penanda kolom di
   tabel, mengganggu di tengah kalimat. */
function tanpaPlus(teks) {
  return teks.replace(/^\+\s*/, "");
}

/* ============================================================
   PROMO - mati sendiri lewat tanggalnya

   Paket boleh punya medan `promo` di mcu.js. Lencananya hanya
   dirender kalau promonya ADA dan BELUM LEWAT tanggal berlakunya.

   Ini disengaja: promo yang dipasang lalu dilupakan adalah cara
   paling mudah bagi situs rumah sakit menjanjikan potongan harga
   yang sudah berakhir setahun lalu. Dengan `berlakuSampai`, ia
   berhenti sendiri tanpa perlu ada yang ingat mencabutnya.

   Promo tanpa `berlakuSampai` dianggap tidak berbatas waktu dan
   tampil terus sampai dicabut manual. Saat ini Screening Ginjal Anak
   memakai bentuk itu - lihat catatan di mcu.js.
   ============================================================ */
function promoAktif(promo) {
  if (!promo?.label) return false;
  if (!promo.berlakuSampai) return true;

  const hariIni = new Date();
  const batas = new Date(`${promo.berlakuSampai}T23:59:59+07:00`);
  return batas >= hariIni;
}

function hargaTerendah() {
  const angka = KATEGORI.flatMap((k) => k.paket.map((p) => p.harga)).filter((h) => h != null);
  return angka.length ? Math.min(...angka) : null;
}

/* Baris varian disembunyikan kalau TIDAK SATU PAKET PUN punya
   angkanya. Baris yang seluruh selnya "-" hanya menambah tinggi
   tabel tanpa memberi tahu apa pun. */
function varianTerpakai(kategori) {
  return kategori.varian.filter((v) => kategori.paket.some((p) => p.varian?.[v.kunci] != null));
}

/* ============================================================
   PEMERIKSA SELISIH - hanya untuk spanduk mode pengembangan

   Tiap tambahan punya selisih yang sama di semua paket: golongan
   darah selalu +70rb, faeces +95rb, thorax +190rb. Pola itu terbukti
   dari posternya sendiri - "Paket A Total +++ 690.000" persis sama
   dengan 335.000 + 70 + 95 + 190.

   Fungsi ini mencari baris yang menyimpang. Saat ditulis, ia
   menemukan satu: Paket B + Foto Thorax tertulis 1.370.000, padahal
   polanya memberi 1.270.000.

   ANGKANYA TIDAK DIPERBAIKI OTOMATIS, dan memang tidak boleh -
   menebak harga rumah sakit lebih berbahaya daripada meneruskan
   salah ketik. Fungsi ini hanya menunjuk.
   ============================================================ */
function selisihJanggal() {
  const janggal = [];

  KATEGORI.forEach((kategori) => {
    kategori.varian.forEach((v) => {
      if (v.kunci === "dasar" || v.kunci === "semua") return;

      const selisih = kategori.paket
        .filter((p) => p.harga != null && p.varian?.[v.kunci] != null)
        .map((p) => ({ nama: `${kategori.nama.id} ${namaPaket(p)}`, nilai: p.varian[v.kunci] - p.harga }));

      /* Butuh minimal tiga paket untuk tahu mana yang "lazim". */
      if (selisih.length < 3) return;

      const hitung = {};
      selisih.forEach((s) => {
        hitung[s.nilai] = (hitung[s.nilai] || 0) + 1;
      });

      const lazim = Number(Object.keys(hitung).sort((a, b) => hitung[b] - hitung[a])[0]);

      selisih.forEach((s) => {
        if (s.nilai !== lazim) {
          janggal.push(
            `${s.nama} ${v.nama.id}: selisihnya ${rupiah(s.nilai)}, sementara paket lain ${rupiah(lazim)}`
          );
        }
      });
    });
  });

  return janggal;
}

/* Nama paket lengkap dengan julukannya. Dipakai di spanduk mode
   pengembangan, dan HARUS unik: MCU Premarital punya dua paket
   bernama sama persis "Paket Silver" - yang membedakan cuma
   julukannya, Pria dan Wanita. Tanpa julukan, dua baris daftar jadi
   kembar dan React mengeluh soal kunci ganda. */
function namaPaket(paket) {
  return paket.julukan ? `${paket.nama.id} - ${paket.julukan.id}` : paket.nama.id;
}

function yangBelumLengkap() {
  const kurang = [];

  KATEGORI.forEach((kategori) => {
    const tanpaHarga = kategori.paket.filter((p) => p.harga == null);

    /* Kalau SELURUH paket satu kategori tidak berharga, itu satu
       masalah - bukan empat. Ditulis sebagai satu baris supaya
       daftarnya tetap bisa dibaca. */
    if (tanpaHarga.length === kategori.paket.length && kategori.paket.length > 0) {
      kurang.push(`harga seluruh paket ${kategori.nama.id} - tidak tercantum di poster`);
    } else {
      tanpaHarga.forEach((p) => kurang.push(`harga ${namaPaket(p)} (${kategori.nama.id})`));
    }

    kategori.paket.forEach((p) => {
      if (!p.isi || p.isi.length === 0) {
        kurang.push(`daftar pemeriksaan ${namaPaket(p)} (${kategori.nama.id})`);
      }
    });
  });

  if (PERSIAPAN.length === 0) {
    kurang.push("daftar persiapan sebelum MCU - hampir semua paket memuat glukosa puasa");
  }
  if (TARIF_DIPERBARUI == null) {
    kurang.push("tanggal konfirmasi tarif - poster sumbernya bertanggal 2022");
  }

  return kurang;
}

/* ---------- SAKLAR SPANDUK MODE PENGEMBANGAN ----------
   false = spanduk merah catatan data disembunyikan.
   Ubah ke true kalau mau memeriksa lagi data yang belum lengkap.
   Di produksi spanduk ini tetap tidak pernah tayang, apa pun isinya. */
const TAMPILKAN_CATATAN_DEV = false;

export default function MCUContent() {
  const { lang, t } = useLanguage();
  const [pratinjau, setPratinjau] = useState(null);

  const kurang = yangBelumLengkap();
  const janggal = selisihJanggal();
  const termurah = rupiah(hargaTerendah());
  const waHref = `https://wa.me/${KONTAK_MCU.cs.wa}`;

  return (
    <div className="w-full bg-[var(--c-surface)] pt-[64px] md:pt-[112px]">

      {/* ---------- BREADCRUMB ---------- */}
      <div className="border-b border-[var(--c-line)] bg-[var(--c-tint)]">
        <div style={{ maxWidth: 1600, margin: "0 auto", padding: "12px 16px" }}>
          <nav aria-label="Breadcrumb" className="text-[13px] sm:text-sm">
            <ol className="flex flex-wrap items-center gap-y-1">
              <li className="flex items-center">
                <Link href="/" className="rounded text-[var(--c-muted)] underline-offset-4 transition-colors hover:text-[var(--c-brand)] hover:underline">
                  {t("header.nav.home")}
                </Link>
                <Pemisah />
              </li>
              <li className="flex items-center">
                {TAUTKAN_INDUK ? (
                  <Link href="/layanan" className="rounded text-[var(--c-muted)] underline-offset-4 transition-colors hover:text-[var(--c-brand)] hover:underline">
                    {t("header.menu.layanan.label")}
                  </Link>
                ) : (
                  <span className="text-[var(--c-muted)]">{t("header.menu.layanan.label")}</span>
                )}
                <Pemisah />
              </li>
              <li className="flex items-center">
                <span aria-current="page" className="font-bold text-[var(--c-brand)]">
                  {pilihTeks(TEKS.judul, lang)}
                </span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* ---------- KEPALA ----------
         Pita berwarna selebar layar, isinya tetap selebar halaman.
         Tanpa gambar sampul: gambar yang tidak ada hubungannya dengan
         harga hanya menunda orang sampai ke angkanya. */}
      <div className="bg-[var(--c-tint)]">
        <div style={{ maxWidth: LEBAR_HALAMAN, margin: "0 auto", padding: "48px 16px 44px" }}>
          <p className="font-semibold uppercase tracking-[0.16em] text-[var(--c-brand-mid)]" style={{ fontSize: 11.5 }}>
            {pilihTeks(TEKS.eyebrow, lang)}
          </p>

          <h1 className="text-balance font-bold leading-[1.08] tracking-tight text-[var(--c-ink)]" style={{ marginTop: 10, fontSize: "clamp(30px, 4.8vw, 46px)" }}>
            {pilihTeks(TEKS.judul, lang)}
          </h1>

          <p className="text-[var(--c-body)]" style={{ marginTop: 14, maxWidth: 660, fontSize: 15.5, lineHeight: 1.7 }}>
            {pilihTeks(TEKS.sub, lang)}
          </p>

          {termurah && (
            <p style={{ marginTop: 18 }}>
              <span className="text-[var(--c-muted)]" style={{ fontSize: 13 }}>
                {pilihTeks(TEKS.mulaiDari, lang)}{" "}
              </span>
              <span className="font-bold text-[var(--c-ink)]" style={{ fontSize: 22 }}>
                {termurah}
              </span>
            </p>
          )}

          {/* Tautan lompat antar kategori. Halaman ini panjang; tanpa
             ini orang harus menggulir melewati seluruh MCU General
             untuk tahu bahwa ada paket sesuai usia. */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 22 }}>
            {KATEGORI.map((k) => (
              <a
                key={k.kunci}
                href={`#${k.kunci}`}
                className="border border-[var(--c-brand-line)] bg-[var(--c-surface)] font-bold text-[var(--c-brand)] transition-colors hover:bg-[var(--c-tint-2)]"
                style={{ borderRadius: 9, padding: "10px 16px", fontSize: 14 }}
              >
                {pilihTeks(k.namaSingkat ?? k.nama, lang)}
              </a>
            ))}
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 22 }}>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-[10px] bg-[var(--c-brand)] font-bold text-white transition-colors hover:bg-[var(--c-brand-strong)]"
              style={{ padding: "12px 20px", fontSize: 14.5 }}
            >
              {pilihTeks(TEKS.hubungiCs, lang)}
              <span aria-hidden="true">&rarr;</span>
            </a>

            <a
              href={`tel:${KONTAK_MCU.telepon.tel}`}
              className="inline-flex items-center gap-2 rounded-[10px] border border-[var(--c-brand-line)] bg-[var(--c-surface)] font-bold text-[var(--c-brand)] transition-colors hover:bg-[var(--c-tint-2)]"
              style={{ padding: "12px 20px", fontSize: 14.5 }}
            >
              {pilihTeks(TEKS.telepon, lang)} {KONTAK_MCU.telepon.tampil}
            </a>
          </div>
        </div>
      </div>

      <section style={{ maxWidth: LEBAR_HALAMAN, margin: "0 auto", padding: "44px 16px 64px" }}>

        {/* ---------- SPANDUK MODE PENGEMBANGAN ----------
           Tidak ikut tayang di produksi. */}
        {TAMPILKAN_CATATAN_DEV && process.env.NODE_ENV !== "production" && (kurang.length > 0 || janggal.length > 0) && (
          <div className="border-l-4 border-red-500 bg-red-50" style={{ marginBottom: 40, borderRadius: 8, padding: "14px 16px" }}>
            <p className="font-bold text-red-800" style={{ fontSize: 13.5 }}>
              Hanya tampil saat pengembangan - bukan bagian dari halaman
            </p>

            {kurang.length > 0 && (
              <>
                <p className="font-semibold text-red-800" style={{ marginTop: 10, fontSize: 12.5 }}>
                  Data yang belum ada
                </p>
                <ul className="text-red-700" style={{ marginTop: 4, fontSize: 13, listStyle: "disc", paddingLeft: 20 }}>
                  {kurang.map((k, i) => (
                    <li key={`${i}-${k}`}>{k}</li>
                  ))}
                </ul>
              </>
            )}

            {janggal.length > 0 && (
              <>
                <p className="font-semibold text-red-800" style={{ marginTop: 12, fontSize: 12.5 }}>
                  Angka yang menyimpang dari pola - konfirmasi ke bagian keuangan
                </p>
                <ul className="text-red-700" style={{ marginTop: 4, fontSize: 13, listStyle: "disc", paddingLeft: 20 }}>
                  {janggal.map((j, i) => (
                    <li key={`${i}-${j}`}>{j}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}

        {/* ---------- KATEGORI ---------- */}
        {KATEGORI.map((kategori, i) => (
          <BagianKategori
            key={kategori.kunci}
            kategori={kategori}
            lang={lang}
            pertama={i === 0}
            onPratinjau={(indeks) => setPratinjau({ kunci: kategori.kunci, daftar: kategori.poster, indeks })}
          />
        ))}

        {/* ---------- CATATAN UMUM ---------- */}
        {CATATAN_UMUM.length > 0 && (
          <>
            <h2 className="font-bold text-[var(--c-ink)]" style={{ marginTop: 64, fontSize: 22 }}>
              {pilihTeks(TEKS.catatan, lang)}
            </h2>
            <ul style={{ marginTop: 16, maxWidth: 720, display: "grid", gap: 10 }}>
              {CATATAN_UMUM.map((c, i) => (
                <li key={i} className="text-slate-700" style={{ display: "flex", gap: 10, fontSize: 14.5, lineHeight: 1.7 }}>
                  <span aria-hidden="true" className="font-bold text-[var(--c-brand-mid)]">&bull;</span>
                  <span>{pilihTeks(c, lang)}</span>
                </li>
              ))}
            </ul>
          </>
        )}

        {/* ---------- PERSIAPAN ----------
           Muncul sendiri begitu PERSIAPAN di mcu.js diisi. */}
        {PERSIAPAN.length > 0 && (
          <>
            <h2 className="font-bold text-[var(--c-ink)]" style={{ marginTop: 56, fontSize: 22 }}>
              {pilihTeks(TEKS.persiapan, lang)}
            </h2>
            <ol style={{ marginTop: 16, maxWidth: 720, display: "grid", gap: 10, listStyle: "decimal", paddingLeft: 22 }}>
              {PERSIAPAN.map((p, i) => (
                <li key={i} className="text-slate-700" style={{ fontSize: 14.5, lineHeight: 1.7 }}>
                  {pilihTeks(p, lang)}
                </li>
              ))}
            </ol>
          </>
        )}

        {/* ---------- RESERVASI ---------- */}
        <Reservasi lang={lang} />
      </section>

      <Footer />

      {pratinjau && (
        <Pratinjau
          daftar={pratinjau.daftar}
          kunci={pratinjau.kunci}
          indeks={pratinjau.indeks}
          lang={lang}
          onTutup={() => setPratinjau(null)}
          onPindah={(i) => setPratinjau({ ...pratinjau, indeks: i })}
        />
      )}
    </div>
  );
}

/* ============================================================
   SATU KATEGORI - kartu, akordeon, tabel, gratis, poster

   Keadaan akordeon disimpan di sini, bukan di komponen induk. Dua
   kategori berarti dua akordeon yang saling tidak berhubungan;
   menaruhnya di induk berarti membuka Paket C menutup panel di
   kategori sebelah.
   ============================================================ */

function BagianKategori({ kategori, lang, pertama, onPratinjau }) {
  const [terbuka, setTerbuka] = useState(kategori.paket[0]?.kode ?? null);

  const kolomVarian = varianTerpakai(kategori);

  /* Tabel perbandingan baru berguna kalau ada yang DIBANDINGKAN.
     Kategori berisi satu paket menghasilkan tabel satu kolom - itu
     bukan tabel, itu daftar yang dibingkai. Untuk kategori seperti
     itu, kartu paket dan catatan di bawahnya sudah menyampaikan hal
     yang sama dengan lebih ringkas. */
  const adaTabel = kolomVarian.length > 0 && kategori.paket.length > 1;

  /* Kalau tidak satu paket pun punya daftar pemeriksaan - MCU
     Premarital, misalnya, yang posternya cuma memuat harga - seluruh
     bagian "Rincian Pemeriksaan" tidak dirender. Akordeon berisi lima
     panel yang semuanya berbunyi "tersedia di customer service" hanya
     membuang gulir orang tanpa memberi tahu apa pun; keterangan yang
     sama sudah ada di catatan kategori, satu kali. */
  const adaRincian = kategori.paket.some((p) => p.isi?.length > 0);

  function bukaRincian(kode) {
    setTerbuka(kode);
    requestAnimationFrame(() => {
      document
        .getElementById(`rincian-${kategori.kunci}-${kode}`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  return (
    <section id={kategori.kunci} style={{ marginTop: pertama ? 0 : 72, scrollMarginTop: 120 }}>
      <h2 className="font-bold leading-tight text-[var(--c-ink)]" style={{ fontSize: 28 }}>
        {pilihTeks(kategori.nama, lang)}
      </h2>
      <p className="text-[var(--c-body)]" style={{ marginTop: 10, maxWidth: 660, fontSize: 15, lineHeight: 1.7 }}>
        {pilihTeks(kategori.ringkas, lang)}
      </p>

      {/* ---------- KARTU PAKET ----------
         minmax 330px: di layar 1140px muat tiga per baris. Dengan
         angka lebih kecil ia muat empat, dan barisan terakhir tinggal
         satu-dua kartu melar - terlihat seperti ada yang belum
         selesai dimuat. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(330px, 1fr))", gap: 18, marginTop: 22 }}>
        {kategori.paket.map((p) => (
          <KartuPaket
            key={p.kode}
            paket={p}
            lang={lang}
            onRincian={adaRincian ? () => bukaRincian(p.kode) : null}
          />
        ))}
      </div>

      {/* ---------- AKORDEON ----------
         Panel tertutup tetap dirender di DOM dengan `hidden`, bukan
         dilepas. Dengan begitu Ctrl+F peramban dan mesin pencari
         tetap menemukan nama pemeriksaannya. */}
      {adaRincian && (
        <>
          <h3 className="font-bold text-[var(--c-ink)]" style={{ marginTop: 44, fontSize: 19 }}>
            {pilihTeks(TEKS.rincian, lang)}
          </h3>

          <div style={{ marginTop: 14, borderTop: "1px solid #e2e8f0" }}>
            {kategori.paket.map((p) => (
              <PanelRincian
                key={p.kode}
                paket={p}
                kunciKategori={kategori.kunci}
                lang={lang}
                terbuka={terbuka === p.kode}
                onToggle={() => setTerbuka(terbuka === p.kode ? null : p.kode)}
              />
            ))}
          </div>
        </>
      )}

      {/* ---------- TABEL ----------
         Hanya untuk kategori yang punya pilihan tambahan. */}
      {adaTabel && <TabelHarga kategori={kategori} kolomVarian={kolomVarian} lang={lang} />}

      {/* ---------- GRATIS ----------
         Cakupan tiap butir ditulis sebagai label kecil, bukan satu
         kalimat pukul rata di bawah daftar - kalau disamaratakan,
         yang membeli paket termurah mengira dapat semuanya juga. */}
      {kategori.gratis?.length > 0 && (
        <>
          <h3 className="font-bold text-[var(--c-ink)]" style={{ marginTop: 44, fontSize: 19 }}>
            {pilihTeks(TEKS.gratis, lang)}
          </h3>

          <ul style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12, marginTop: 16 }}>
            {kategori.gratis.map((g, i) => (
              <li key={i} className="border border-[var(--c-line)] bg-[var(--c-tint)]" style={{ borderRadius: 10, padding: "14px 16px" }}>
                <p className="font-semibold text-[var(--c-ink)]" style={{ fontSize: 14 }}>
                  {pilihTeks(g.nama, lang)}
                </p>
                <p className="font-semibold uppercase tracking-[0.1em] text-[var(--c-brand-mid)]" style={{ marginTop: 6, fontSize: 10.5 }}>
                  {pilihTeks(g.untuk, lang)}
                </p>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* ---------- CATATAN KATEGORI ---------- */}
      {kategori.catatan?.length > 0 && (
        <ul style={{ marginTop: 20, maxWidth: 720, display: "grid", gap: 10 }}>
          {kategori.catatan.map((c, i) => (
            <li key={i} className="text-slate-700" style={{ display: "flex", gap: 10, fontSize: 14.5, lineHeight: 1.7 }}>
              <span aria-hidden="true" className="font-bold text-[var(--c-brand-mid)]">&bull;</span>
              <span>{pilihTeks(c, lang)}</span>
            </li>
          ))}
        </ul>
      )}

      {/* ---------- POSTER ---------- */}
      {kategori.poster?.length > 0 && (
        <>
          <h3 className="font-bold text-[var(--c-ink)]" style={{ marginTop: 44, fontSize: 19 }}>
            {pilihTeks(TEKS.poster, lang)}
          </h3>
          <p className="text-[var(--c-muted)]" style={{ marginTop: 8, fontSize: 13.5 }}>
            {pilihTeks(TEKS.posterSub, lang)}
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16, marginTop: 16 }}>
            {kategori.poster.map((p, i) => (
              <button
                key={i}
                id={`poster-${kategori.kunci}-${i}`}
                type="button"
                onClick={() => onPratinjau(i)}
                className="group block w-full overflow-hidden border border-[var(--c-line)] text-left transition-colors hover:border-[var(--c-brand-line)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand)]"
                style={{ borderRadius: 12 }}
              >
                <Image
                  src={p.gambar}
                  alt={pilihTeks(p.alt, lang)}
                  placeholder="blur"
                  sizes="(min-width: 768px) 360px, 100vw"
                  className="h-auto w-full transition-transform duration-500 ease-out group-hover:scale-[1.02] motion-reduce:transform-none"
                />
                {p.judul && (
                  <p className="border-t border-[var(--c-line)] font-semibold text-[var(--c-body)]" style={{ padding: "10px 14px", fontSize: 12.5 }}>
                    {pilihTeks(p.judul, lang)}
                  </p>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

/* ============================================================
   TABEL HARGA PAKET DAN TAMBAHAN - dua bentuk, satu data

   ============================================================
   KENAPA BUKAN SATU TABEL SAJA

   Sebelumnya bagian ini satu bentuk untuk semua layar: <div dengan
   overflowX auto> membungkus <table minWidth 980>, kolom pertama
   dibuat lengket.

   Di layar lebar itu benar. Di ponsel 380px:

     - kolom "Pemeriksaan" lengket dengan whiteSpace nowrap memakan
       sekitar 250px, tersisa 130px untuk enam kolom harga
     - yang terlihat cuma potongan kolom Paket A
     - TIDAK ADA TANDA bahwa tabelnya bisa digeser. Tidak ada bayangan
       di tepi, dan di iOS bilah gulirnya tidak terlihat sampai
       disentuh. Pengunjung menyimpulkan harganya memang tidak
       dicantumkan - persis kebalikan dari tujuan halaman ini.

   Menyetel lebar kolom tidak menyelesaikannya. Tabel tujuh kolom
   memang tidak muat di layar selebar telapak tangan, berapa pun
   kecilnya hurufnya dibuat.

   ============================================================
   AMBANGNYA lg, BUKAN md

     >= 1024px   tabel penuh, tidak berubah dari semula. Lebar isi
                 halaman 1140 - padding 32 = 1108, di atas minWidth
                 980, jadi tidak pernah tergeser menyamping.

     < 1024px    pemilih paket berbentuk pil, lalu daftar
                 "tambahan -> harga" untuk paket yang dipilih saja.
                 Satu kolom, tanpa geser.

   Kalau ambangnya md (768px), tabelnya masih 980px di sana dan tetap
   harus digeser - persis masalah yang sedang diperbaiki.

   ============================================================
   KEDUANYA DIRENDER, SALAH SATUNYA display:none

   Bukan lewat useMediaQuery: hook itu tidak tahu lebar layar saat
   render di server, jadi selalu ada satu frame dengan bentuk yang
   salah setiap halaman dibuka.

   Konsekuensinya angka harga ada dua kali di DOM. Yang tersembunyi
   dengan display:none dilewati pembaca layar, jadi tidak dibacakan
   dua kali. Ctrl+F peramban memang menemukan tiap angka dua kali -
   itu harga yang dibayar, dan lebih murah daripada kedipan bentuk
   di setiap muat halaman.

   ============================================================
   DI PONSEL, BARIS TANPA HARGA TIDAK JADI TANDA HUBUNG

   Paket F hanya punya harga dasar. Di tabel lebar, delapan tanda
   hubung di kolomnya masih berguna - pembaca membandingkannya dengan
   kolom sebelah.

   Di ponsel tidak ada kolom sebelah. Delapan baris berisi "-" hanya
   membuat orang menggulir tanpa mendapat apa pun. Karena itu di
   bentuk ponsel baris tanpa harga dikumpulkan jadi satu kalimat di
   bawah daftar - keterangannya tetap ada, panjangnya tidak.
   ============================================================ */

function TabelHarga({ kategori, kolomVarian, lang }) {
  const [kodeAktif, setKodeAktif] = useState(kategori.paket[0]?.kode ?? null);

  const paket = kategori.paket.find((p) => p.kode === kodeAktif) ?? kategori.paket[0];

  const berharga = kolomVarian.filter((v) => paket?.varian?.[v.kunci] != null);
  const kosong = kolomVarian.filter((v) => paket?.varian?.[v.kunci] == null);

  const catatan = (
    <p className="text-[var(--c-muted)]" style={{ marginTop: 12, fontSize: 13, lineHeight: 1.6 }}>
      {pilihTeks(TEKS.catatanHarga, lang)}
      {TARIF_DIPERBARUI && (
        <>
          {" "}
          {pilihTeks(TEKS.diperbarui, lang)}:{" "}
          {new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "id-ID", { day: "numeric", month: "long", year: "numeric", timeZone: "Asia/Jakarta" }).format(new Date(TARIF_DIPERBARUI))}.
        </>
      )}
    </p>
  );

  return (
    <>
      <h3 className="font-bold text-[var(--c-ink)]" style={{ marginTop: 44, fontSize: 19 }}>
        {pilihTeks(TEKS.tabel, lang)}
      </h3>

      <p className="text-[var(--c-body)]" style={{ marginTop: 10, maxWidth: 720, fontSize: 14, lineHeight: 1.7 }}>
        {pilihTeks(TEKS.tabelKeterangan, lang)}{" "}
        <span className="hidden lg:inline">{pilihTeks(TEKS.tabelKeteranganTanda, lang)}</span>
      </p>

      {/* ---------- BENTUK PONSEL - satu paket sekali lihat ---------- */}
      <div className="lg:hidden">
        {/* role=group + aria-label: tanpa itu pembaca layar
           membacakan enam tombol lepas tanpa memberi tahu ini
           kelompok apa. aria-pressed menandai mana yang aktif -
           warna saja tidak terbaca. */}
        <div role="group" aria-label={pilihTeks(TEKS.pilihPaket, lang)} style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 16 }}>
          {kategori.paket.map((p) => {
            const aktif = p.kode === paket?.kode;

            return (
              <button
                key={p.kode}
                type="button"
                onClick={() => setKodeAktif(p.kode)}
                aria-pressed={aktif}
                className={aktif ? "border-[var(--c-brand)] bg-[var(--c-brand)] font-bold text-white transition-colors" : "border-[var(--c-line)] bg-[var(--c-surface)] font-bold text-[var(--c-brand)] transition-colors hover:bg-[var(--c-tint)]"}
                style={{ borderWidth: 1, borderStyle: "solid", borderRadius: 9, padding: "9px 14px", fontSize: 13.5, whiteSpace: "nowrap" }}
              >
                {pilihTeks(p.nama, lang)}
              </button>
            );
          })}
        </div>

        {paket?.julukan && (
          <p className="font-semibold uppercase tracking-[0.12em] text-[var(--c-brand-mid)]" style={{ marginTop: 14, fontSize: 10.5 }}>
            {pilihTeks(paket.julukan, lang)}
          </p>
        )}

        {/* aria-live: paket diganti, angkanya berubah di tempat yang
           sama. Tanpa ini pembaca layar tidak mengumumkan apa pun dan
           pengguna tidak tahu daftarnya sudah berganti. */}
        <dl aria-live="polite" style={{ marginTop: paket?.julukan ? 10 : 14, borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden" }}>
          {berharga.map((v, i) => {
            const dasar = v.kunci === "dasar";

            return (
              <div
                key={v.kunci}
                style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 14, padding: "13px 14px", backgroundColor: i % 2 ? "#fafafa" : "#ffffff", borderTop: i === 0 ? "none" : "1px solid #e2e8f0" }}
              >
                {/* Nama pemeriksaan BOLEH turun baris di sini - beda
                   dengan tabel, yang butuh nowrap supaya barisnya
                   rata. Yang nowrap cuma sisi angkanya. */}
                <dt className={dasar ? "font-bold text-[var(--c-ink)]" : "font-semibold text-slate-700"} style={{ fontSize: 13.5, lineHeight: 1.45, minWidth: 0 }}>
                  {pilihTeks(v.nama, lang)}
                </dt>
                <dd className="font-bold text-[var(--c-ink)]" style={{ fontSize: dasar ? 17 : 14.5, whiteSpace: "nowrap", margin: 0 }}>
                  {rupiah(paket.varian[v.kunci])}
                </dd>
              </div>
            );
          })}
        </dl>

        {kosong.length > 0 && (
          <p className="text-[var(--c-muted)]" style={{ marginTop: 10, fontSize: 12.5, lineHeight: 1.6 }}>
            {pilihTeks(TEKS.tidakTercantum, lang)}:{" "}
            {kosong.map((v) => tanpaPlus(pilihTeks(v.nama, lang))).join(", ")}.
          </p>
        )}

        {catatan}
      </div>

      {/* ---------- BENTUK LAYAR LEBAR - tabel penuh ----------
         Kolom pertama tetap dibuat lengket. Di >=1024px isinya
         memang tidak pernah tergeser, tapi kalau suatu saat kolom
         paket bertambah, sifat lengket itu yang menyelamatkan. */}
      <div className="hidden lg:block" style={{ marginTop: 16, overflowX: "auto", borderRadius: 12, border: "1px solid #e2e8f0" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 980 }}>
          <thead>
            <tr>
              <th
                scope="col"
                className="text-left font-bold text-[var(--c-ink)]"
                style={{ position: "sticky", left: 0, zIndex: 1, backgroundColor: "#f1faf5", padding: "13px 16px", fontSize: 13.5, whiteSpace: "nowrap" }}
              >
                {pilihTeks(TEKS.pemeriksaan, lang)}
              </th>
              {kategori.paket.map((p) => (
                <th key={p.kode} scope="col" className="text-right font-bold text-[var(--c-ink)]" style={{ backgroundColor: "#f1faf5", padding: "13px 16px", fontSize: 13.5, whiteSpace: "nowrap" }}>
                  {pilihTeks(p.nama, lang)}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {kolomVarian.map((v, i) => {
              const latar = i % 2 ? "#fafafa" : "#ffffff";
              const barisDasar = v.kunci === "dasar";

              return (
                <tr key={v.kunci} style={{ borderTop: "1px solid #e2e8f0" }}>
                  <th
                    scope="row"
                    className={barisDasar ? "text-left font-bold text-[var(--c-ink)]" : "text-left font-semibold text-slate-700"}
                    style={{ position: "sticky", left: 0, zIndex: 1, backgroundColor: latar, padding: "12px 16px", fontSize: 13.5, whiteSpace: "nowrap" }}
                  >
                    {pilihTeks(v.nama, lang)}
                  </th>

                  {kategori.paket.map((p) => {
                    const harga = rupiah(p.varian?.[v.kunci]);
                    const kelas = !harga
                      ? "text-right text-[var(--c-faint)]"
                      : barisDasar
                        ? "text-right font-bold text-[var(--c-ink)]"
                        : "text-right text-[var(--c-ink)]";

                    return (
                      <td key={p.kode} className={kelas} style={{ backgroundColor: latar, padding: "12px 16px", fontSize: 13.5, whiteSpace: "nowrap" }}>
                        {harga ?? "-"}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="hidden lg:block">{catatan}</div>
    </>
  );
}

/* ============================================================
   KARTU PAKET - ringkas

   Isi pemeriksaan TIDAK ditaruh di sini. Paket terpanjang punya 27
   baris; memasukkannya ke kartu membuat kartu-kartunya timpang dan
   harganya - hal yang paling dicari orang - terdorong jauh ke bawah
   layar.
   ============================================================ */

function KartuPaket({ paket, lang, onRincian }) {
  const harga = rupiah(paket.harga);

  return (
    <article className="border border-[var(--c-line)] transition-colors hover:border-[var(--c-brand-line)]" style={{ display: "flex", flexDirection: "column", borderRadius: 14, padding: "20px 18px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <Lencana>{paket.kode}</Lencana>

        {promoAktif(paket.promo) && (
          <span className="border border-amber-300 bg-amber-50 font-bold uppercase tracking-[0.08em] text-amber-800" style={{ borderRadius: 6, padding: "4px 8px", fontSize: 10.5, whiteSpace: "nowrap" }}>
            {pilihTeks(paket.promo.label, lang)}
          </span>
        )}
        <div style={{ minWidth: 0 }}>
          <p className="font-bold leading-tight text-[var(--c-ink)]" style={{ fontSize: 16.5 }}>
            {pilihTeks(paket.nama, lang)}
          </p>
          {paket.julukan && (
            <p className="font-semibold uppercase tracking-[0.12em] text-[var(--c-brand-mid)]" style={{ marginTop: 3, fontSize: 10.5 }}>
              {pilihTeks(paket.julukan, lang)}
            </p>
          )}
        </div>
      </div>

      <div style={{ marginTop: 16, flex: "1 1 auto" }}>
        {harga ? (
          <p className="font-bold text-[var(--c-ink)]" style={{ fontSize: 23 }}>
            {harga}
          </p>
        ) : (
          <p className="font-bold text-[var(--c-brand)]" style={{ fontSize: 18 }}>
            {pilihTeks(TEKS.hubungi, lang)}
          </p>
        )}

        {paket.isi.length > 0 && (
          <p className="text-[var(--c-muted)]" style={{ marginTop: 6, fontSize: 13 }}>
            {paket.isi.length} {pilihTeks(TEKS.pemeriksaanJml, lang)}
          </p>
        )}

        {paket.catatan && (
          <p className="text-[var(--c-muted)]" style={{ marginTop: 10, fontSize: 12.5, lineHeight: 1.55 }}>
            {pilihTeks(paket.catatan, lang)}
          </p>
        )}
      </div>

      {/* Tombolnya hilang kalau kategori ini memang tidak punya
         rincian untuk dituju - tombol yang tidak membawa ke mana-mana
         lebih buruk daripada tidak ada tombol. */}
      {onRincian && (
        <button
          type="button"
          onClick={onRincian}
          className="font-bold text-[var(--c-brand)] underline underline-offset-4 transition-colors hover:text-[var(--c-ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand)]"
          style={{ marginTop: 18, fontSize: 13.5, textAlign: "left" }}
        >
          {pilihTeks(TEKS.lihatRincian, lang)}
        </button>
      )}
    </article>
  );
}

/* ============================================================
   PANEL RINCIAN

   Daftar pemeriksaan dipecah jadi beberapa kolom lewat CSS columns
   dengan lebar minimum 230px - kolomnya bertambah sendiri di layar
   lebar dan menyusut jadi satu di ponsel, tanpa media query. Untuk
   daftar 27 baris, satu kolom panjang membuat orang kehilangan
   tempatnya saat menggulir.
   ============================================================ */

function PanelRincian({ paket, kunciKategori, lang, terbuka, onToggle }) {
  const idPanel = `rincian-${kunciKategori}-${paket.kode}`;

  return (
    <div id={idPanel} style={{ borderBottom: "1px solid #e2e8f0", scrollMarginTop: 120 }}>
      <h4>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={terbuka}
          aria-controls={`${idPanel}-isi`}
          className="w-full text-left transition-colors hover:bg-[var(--c-tint)] focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--c-brand)]"
          style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 8px" }}
        >
          <Lencana aktif={terbuka}>{paket.kode}</Lencana>

          <span className="font-bold text-[var(--c-ink)]" style={{ fontSize: 15.5, flex: "1 1 auto", minWidth: 0 }}>
            {pilihTeks(paket.nama, lang)}
            {paket.julukan && (
              <span className="font-semibold text-[var(--c-faint)]"> &middot; {pilihTeks(paket.julukan, lang)}</span>
            )}
          </span>

          <span className="text-[var(--c-muted)]" style={{ fontSize: 13, whiteSpace: "nowrap" }}>
            {paket.isi.length > 0 ? `${paket.isi.length} ${pilihTeks(TEKS.pemeriksaanJml, lang)}` : ""}
          </span>

          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="shrink-0 text-[var(--c-brand)] transition-transform duration-300 motion-reduce:transition-none" style={{ width: 18, height: 18, transform: terbuka ? "rotate(180deg)" : "none" }}>
            <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </h4>

      <div id={`${idPanel}-isi`} hidden={!terbuka} style={{ padding: "0 8px 22px 8px" }}>
        {paket.isi.length === 0 ? (
          <p className="text-[var(--c-muted)]" style={{ fontSize: 14 }}>
            {pilihTeks(TEKS.belumAda, lang)}
          </p>
        ) : (
          <ol className="text-slate-700" style={{ columns: "230px", columnGap: 32, listStyle: "decimal", paddingLeft: 22, margin: 0 }}>
            {paket.isi.map((butir, i) => (
              <li key={i} style={{ breakInside: "avoid", marginBottom: 7, fontSize: 14, lineHeight: 1.55 }}>
                {pilihTeks(butir, lang)}
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

/* Lencana berbentuk pil, bukan lingkaran berukuran tetap: kodenya
   bisa "A" (satu huruf) atau ">40-50" (enam karakter). */
function Lencana({ children, aktif = true }) {
  return (
    <span
      className="font-bold text-white"
      style={{
        backgroundColor: aktif ? "#0f6a3b" : "#8fc9a9",
        borderRadius: 8,
        padding: "5px 10px",
        fontSize: 12.5,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
        flex: "0 0 auto",
      }}
    >
      {children}
    </span>
  );
}

/* ============================================================
   RESERVASI - pilih paket dan tambahan, pesan WhatsApp terisi

   ============================================================
   BOLEH PILIH BANYAK, TAPI TOTALNYA TIDAK SELALU MUNCUL

   Poster hanya memberi harga "paket + SATU tambahan". Tidak ada harga
   untuk paket + dua tambahan.

   Selisih tiap tambahan memang terlihat tetap di semua paket - jadi
   menjumlahkan sendiri terasa aman. TIDAK. Pola itu sudah terbukti
   bocor sekali: Paket B + Foto Thorax menyimpang Rp 100.000. Kalau
   satu baris bisa menyimpang, penjumlahan bebas juga bisa meleset,
   dan yang menanggung adalah pasien di depan kasir.

   Aturannya:

     0 tambahan                    -> harga paket dasar   (dari tabel)
     1 tambahan                    -> harga paket+tambahan(dari tabel)
     semua tambahan, dan paket
     punya angka "total +++"       -> angka itu           (dari tabel)
     selain itu                    -> "akan dikonfirmasi CS"

   Tidak ada satu pun angka yang dihitung di sini. Semuanya diambil.

   Paket MCU Sesuai Usia tidak punya harga di poster mana pun, jadi
   apa pun yang dipilih di sana berujung "akan dikonfirmasi CS" -
   dan itu jawaban yang jujur, bukan kegagalan.

   KALAU NANTI KEUANGAN MENGONFIRMASI tarif per tambahan, tambahkan
   medan `tarif` pada tiap entri varian di mcu.js, lalu ganti cabang
   terakhir hitungTotal() dengan penjumlahan. Bagian lain tidak perlu
   diubah.

   ============================================================
   PAP SMEAR DAN PSA SALING MENIADAKAN

   Satu untuk pasien wanita, satu untuk pria di atas 50 tahun.
   Mencentang salah satunya melepas yang lain - bukan memunculkan
   pesan galat setelah orang terlanjur memilih.
   ============================================================ */

const SALING_MENIADAKAN = [["papSmear", "psa"]];

function hitungTotal(paket, dipilih, tersedia) {
  if (dipilih.length === 0) return paket?.varian?.dasar ?? paket?.harga ?? null;
  if (dipilih.length === 1) return paket?.varian?.[dipilih[0]] ?? null;

  if (dipilih.length === tersedia.length && paket?.varian?.semua != null) {
    return paket.varian.semua;
  }

  return null;
}

function Reservasi({ lang }) {
  const pertama = KATEGORI[0]?.paket[0];
  const [pilihan, setPilihan] = useState(
    pertama ? `${KATEGORI[0].kunci}::${pertama.kode}` : ""
  );
  const [tambahan, setTambahan] = useState([]);

  const [kunciKategori, kodePaket] = pilihan.split("::");
  const kategori = KATEGORI.find((k) => k.kunci === kunciKategori) ?? KATEGORI[0];
  const paket = kategori?.paket.find((p) => p.kode === kodePaket) ?? kategori?.paket[0];

  const tersedia = (kategori?.varian ?? []).filter(
    (v) => v.kunci !== "dasar" && v.kunci !== "semua" && paket?.varian?.[v.kunci] != null
  );

  /* Tambahan yang tidak ada pada paket terpilih diabaikan - misalnya
     USG dipilih di Paket C lalu pindah ke paket sesuai usia. Disaring
     saat render, bukan lewat useEffect: satu sumber kebenaran, tidak
     ada kedipan nilai lama. */
  const dipilih = tersedia.filter((v) => tambahan.includes(v.kunci)).map((v) => v.kunci);
  const total = hitungTotal(paket, dipilih, tersedia);

  function ubah(kunci) {
    setTambahan((sebelumnya) => {
      if (sebelumnya.includes(kunci)) return sebelumnya.filter((k) => k !== kunci);

      const lawan = SALING_MENIADAKAN.find((pasangan) => pasangan.includes(kunci))?.find(
        (k) => k !== kunci
      );

      const bersih = lawan ? sebelumnya.filter((k) => k !== lawan) : sebelumnya;
      return [...bersih, kunci];
    });
  }

  const daftarTambahan = dipilih.length
    ? tersedia
        .filter((v) => dipilih.includes(v.kunci))
        /* Tanda "+" berguna di tabel, mengganggu di kalimat pesan. */
        .map((v) => tanpaPlus(pilihTeks(v.nama, lang)))
        .join(", ")
    : pilihTeks(TEKS.tanpaTambahan, lang);

  const barisTotal = rupiah(total) ?? pilihTeks(TEKS.akanDikonfirmasi, lang);

  /* Baris promo hanya ikut kalau paket yang dipilih memang sedang
     berpromo - kalau tidak, barisnya tidak ada sama sekali, bukan
     ditulis "tidak ada promo". Pesan yang dikirim pasien sebaiknya
     hanya berisi hal yang berlaku baginya. */
  /* Kalau totalnya tidak bisa ditampilkan - paket tanpa harga di
     poster, atau dua tambahan atau lebih - pasien perlu ikut
     MENANYAKAN harganya, bukan cuma jadwalnya. Tanpa ini, pesannya
     hanya menanyakan jadwal padahal yang belum dia ketahui justru
     harganya, dan CS harus menebak apa yang sebenarnya dia butuhkan. */
  const penutup = rupiah(total)
    ? pilihTeks(TEKS.penutupJadwal, lang)
    : pilihTeks(TEKS.penutupHarga, lang);

  const barisPromo = promoAktif(paket?.promo)
    ? `\n${pilihTeks(TEKS.labelPromo, lang)}: ${pilihTeks(paket.promo.label, lang)}`
    : "";

  /* Pesannya pendek dan masih bisa disunting sebelum dikirim -
     WhatsApp hanya mengisi kolom ketik, tidak mengirim. */
  const pesan =
    lang === "en"
      ? `Hello RS JMC, I would like to book a Medical Check Up.\n\nCategory: ${pilihTeks(kategori?.nama, lang)}\nPackage: ${pilihTeks(paket?.nama, lang)}${paket?.julukan ? ` (${pilihTeks(paket.julukan, lang)})` : ""}\nAdd-ons: ${daftarTambahan}${barisPromo}\nTotal: ${barisTotal}\n\n${penutup}`
      : `Halo RS JMC, saya ingin reservasi Medical Check Up.\n\nKategori: ${pilihTeks(kategori?.nama, lang)}\nPaket: ${pilihTeks(paket?.nama, lang)}${paket?.julukan ? ` (${pilihTeks(paket.julukan, lang)})` : ""}\nTambahan: ${daftarTambahan}${barisPromo}\nTotal: ${barisTotal}\n\n${penutup}`;

  const href = `https://wa.me/${KONTAK_MCU.cs.wa}?text=${encodeURIComponent(pesan)}`;

  return (
    <div id="reservasi" className="bg-[var(--c-tint)]" style={{ marginTop: 64, borderRadius: 14, padding: "28px 24px", scrollMarginTop: 120 }}>
      <p className="font-bold text-[var(--c-ink)]" style={{ fontSize: 18 }}>
        {pilihTeks(TEKS.reservasi, lang)}
      </p>
      <p className="text-[var(--c-body)]" style={{ marginTop: 6, maxWidth: 520, fontSize: 14.5, lineHeight: 1.65 }}>
        {pilihTeks(TEKS.reservasiSub, lang)}
      </p>

      {/* ---------- PAKET ----------
         <optgroup> mengelompokkan per kategori, jadi satu daftar tetap
         terbaca meski isinya sebelas paket dari dua keluarga berbeda.

         <select> bawaan, bukan menu buatan sendiri: di ponsel ia
         memunculkan pemilih bawaan sistem, dan papan ketik serta
         pembaca layar sudah bekerja tanpa ditambah apa pun. */}
      <div style={{ marginTop: 20, maxWidth: 400 }}>
        <label htmlFor="pilih-paket" className="font-semibold text-slate-700" style={{ fontSize: 13 }}>
          {pilihTeks(TEKS.labelPaket, lang)}
        </label>
        <select
          id="pilih-paket"
          value={pilihan}
          onChange={(e) => setPilihan(e.target.value)}
          className="text-[var(--c-ink)]"
          style={{ width: "100%", marginTop: 6, borderRadius: 10, border: "1px solid #e2e8f0", backgroundColor: "#ffffff", padding: "11px 12px", fontSize: 14.5, outline: "none" }}
        >
          {KATEGORI.map((k) => (
            <optgroup key={k.kunci} label={pilihTeks(k.nama, lang)}>
              {k.paket.map((p) => (
                <option key={p.kode} value={`${k.kunci}::${p.kode}`}>
                  {pilihTeks(p.nama, lang)}
                  {p.julukan ? ` - ${pilihTeks(p.julukan, lang)}` : ""}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      {/* ---------- TAMBAHAN ----------
         fieldset + legend, bukan sekadar <p> di atas deretan kotak
         centang. Tanpa itu, pembaca layar membacakan kotak-kotak lepas
         tanpa memberi tahu ini kelompok apa. */}
      {tersedia.length > 0 && (
        <fieldset style={{ marginTop: 20, border: 0, padding: 0, margin: 0 }}>
          <legend className="font-semibold text-slate-700" style={{ fontSize: 13, padding: 0 }}>
            {pilihTeks(TEKS.labelTambahan, lang)}
          </legend>

          <p className="text-[var(--c-muted)]" style={{ marginTop: 4, maxWidth: 520, fontSize: 12.5, lineHeight: 1.55 }}>
            {pilihTeks(TEKS.petunjukTambahan, lang)}
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 8, marginTop: 12 }}>
            {tersedia.map((v) => {
              const aktif = dipilih.includes(v.kunci);

              return (
                <label
                  key={v.kunci}
                  className={aktif ? "border-[var(--c-brand)] bg-[var(--c-surface)]" : "border-transparent bg-white/60 hover:bg-white"}
                  style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", borderWidth: 1, borderStyle: "solid", borderRadius: 10, padding: "10px 12px" }}
                >
                  <input
                    type="checkbox"
                    checked={aktif}
                    onChange={() => ubah(v.kunci)}
                    className="accent-[var(--c-brand)]"
                    style={{ width: 16, height: 16, flex: "0 0 auto" }}
                  />
                  <span className="text-slate-700" style={{ fontSize: 13.5, lineHeight: 1.4 }}>
                    {tanpaPlus(pilihTeks(v.nama, lang))}
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
      )}

      {/* aria-live: pembaca layar mengumumkan totalnya saat berubah.
         Tanpa itu, pengguna mengganti pilihan dan tidak pernah tahu
         angkanya ikut berubah. */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 16, marginTop: 24 }}>
        <p aria-live="polite" style={{ minWidth: 0 }}>
          <span className="text-[var(--c-muted)]" style={{ fontSize: 13 }}>
            {pilihTeks(TEKS.totalnya, lang)}{" "}
          </span>
          {rupiah(total) ? (
            <span className="font-bold text-[var(--c-ink)]" style={{ fontSize: 26 }}>
              {rupiah(total)}
            </span>
          ) : (
            <span className="font-bold text-[var(--c-brand)]" style={{ fontSize: 16 }}>
              {pilihTeks(TEKS.akanDikonfirmasi, lang)}
            </span>
          )}
        </p>

        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-[10px] bg-[var(--c-brand)] font-bold text-white transition-colors hover:bg-[var(--c-brand-strong)]"
          style={{ padding: "12px 20px", fontSize: 14.5 }}
        >
          {pilihTeks(TEKS.lanjutWa, lang)}
          <span aria-hidden="true">&rarr;</span>
        </a>
      </div>

      <p className="text-[var(--c-muted)]" style={{ marginTop: 14, maxWidth: 560, fontSize: 12.5, lineHeight: 1.6 }}>
        {pilihTeks(TEKS.catatanReservasi, lang)}
      </p>
    </div>
  );
}

/* ============================================================
   PRATINJAU POSTER

   Dibuka menumpuk di halaman yang sama, bukan di tab baru - untuk
   gambar yang cuma ingin dilihat sebentar, tab baru memindahkan orang
   keluar dari halaman tanpa alasan.

   Bisa ditutup dengan tiga cara: tombol silang, Esc, dan menekan
   latarnya. Ketiganya perlu; jendela yang tidak jelas cara menutupnya
   terasa seperti jebakan.

   YANG DIURUS SELAIN TAMPILAN:

     - gulir halaman dikunci selama jendela terbuka. Tanpa itu, roda
       tetikus menggulir halaman DI BALIK jendelanya.
     - fokus papan ketik pindah ke tombol tutup saat terbuka, dan
       DIKEMBALIKAN ke poster yang ditekan saat ditutup.
     - role="dialog" + aria-modal memberi tahu pembaca layar bahwa isi
       di belakangnya sedang tidak aktif.

   Tombol panah hanya berpindah di dalam satu kategori - tidak
   melompat dari poster General ke poster Sesuai Usia.
   ============================================================ */

function Pratinjau({ daftar, kunci, indeks, lang, onTutup, onPindah }) {
  const tombolTutup = useRef(null);
  const poster = daftar[indeks];
  const banyak = daftar.length > 1;

  useEffect(() => {
    tombolTutup.current?.focus();

    const gulirSemula = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const tekan = (e) => {
      if (e.key === "Escape") onTutup();
      if (!banyak) return;
      if (e.key === "ArrowRight") onPindah((indeks + 1) % daftar.length);
      if (e.key === "ArrowLeft") onPindah((indeks - 1 + daftar.length) % daftar.length);
    };

    window.addEventListener("keydown", tekan);

    return () => {
      window.removeEventListener("keydown", tekan);
      document.body.style.overflow = gulirSemula;
      document.getElementById(`poster-${kunci}-${indeks}`)?.focus();
    };
  }, [indeks, banyak, daftar.length, kunci, onTutup, onPindah]);

  if (!poster) return null;

  return (
    /* Menekan latar menutup; menekan gambarnya tidak - karena itu ada
       stopPropagation di kotak isinya. */
    <div
      role="dialog"
      aria-modal="true"
      aria-label={pilihTeks(poster.judul, lang) || pilihTeks(TEKS.poster, lang)}
      onClick={onTutup}
      style={{ position: "fixed", inset: 0, zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(15, 4, 12, 0.86)", padding: 16 }}
    >
      {/* width: fit-content - kotaknya menyusut mengikuti gambar.

         Sebelumnya kotak ini dipaksa selebar 1000px lalu gambarnya
         diperkecil di dalamnya agar muat tingginya; sisa ruang di
         kiri-kanan jadi latar putih yang menganga. Poster potret
         paling parah, karena selisihnya paling besar.

         Sekarang gambarnya yang menentukan: tingginya dibatasi 82vh,
         lebarnya menyesuaikan sendiri mengikuti perbandingan asli -
         peramban selalu menjaga rasio gambar kalau salah satu sisinya
         dibatasi dan sisi lainnya auto. Latar putihnya juga dilepas,
         jadi tidak ada lagi bidang kosong yang bisa terlihat. */}
      <div onClick={(e) => e.stopPropagation()} style={{ position: "relative", width: "fit-content", maxWidth: "100%" }}>
        <Image
          src={poster.gambar}
          alt={pilihTeks(poster.alt, lang)}
          sizes="90vw"
          className="rounded-lg"
          style={{ display: "block", width: "auto", height: "auto", maxWidth: "100%", maxHeight: "82vh" }}
        />

        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 12 }}>
          <p className="font-semibold text-white/80" style={{ fontSize: 13 }}>
            {pilihTeks(poster.judul, lang)}
          </p>

          {banyak && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="text-white/60" style={{ fontSize: 12.5 }}>
                {indeks + 1} / {daftar.length}
              </span>
              <button
                type="button"
                onClick={() => onPindah((indeks - 1 + daftar.length) % daftar.length)}
                aria-label={pilihTeks(TEKS.sebelumnya, lang)}
                className="bg-white/15 font-bold text-white transition-colors hover:bg-white/25"
                style={{ borderRadius: 8, padding: "8px 14px", fontSize: 13 }}
              >
                &larr;
              </button>
              <button
                type="button"
                onClick={() => onPindah((indeks + 1) % daftar.length)}
                aria-label={pilihTeks(TEKS.berikutnya, lang)}
                className="bg-white/15 font-bold text-white transition-colors hover:bg-white/25"
                style={{ borderRadius: 8, padding: "8px 14px", fontSize: 13 }}
              >
                &rarr;
              </button>
            </div>
          )}
        </div>

        <button
          ref={tombolTutup}
          type="button"
          onClick={onTutup}
          aria-label={pilihTeks(TEKS.tutup, lang)}
          className="bg-[var(--c-surface)] text-[var(--c-ink)] shadow-lg transition-colors hover:bg-[var(--c-tint)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          style={{ position: "absolute", top: -14, right: -6, display: "inline-flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: 999 }}
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ width: 18, height: 18 }}>
            <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function Pemisah() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="mx-2 h-3.5 w-3.5 shrink-0 text-[var(--c-faint)] sm:mx-2.5">
      <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
